import type { ImportEingabe, ImportZeile, MindmapSpalte, SpaltenTyp } from '@beholder/shared/module/mindmap';
import { pfadText, reihenfolge, type Baum } from './baum';
import { csvSchreiben, xlsxSchreiben, type Blatt, type GeleseneTabelle, type Zelle } from './format';
import { anordnen, kante, MASS, schrift } from './layout';
import { FARBTON, KLAMMER_PFAD, NOTIZ_PFAD, SYMBOL_PFAD } from './symbole';

export function dateiName(titel: string, endung: string): string {
  const n = titel.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Mindmap';
  return `${n}.${endung}`;
}

export function herunterladen(name: string, inhalt: Blob) {
  const url = URL.createObjectURL(inhalt);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ------------------------------------------------------------------ Tabelle → Datei

export function alsBlatt(titel: string, baum: Baum, spalten: MindmapSpalte[], personen: Map<string, string>): Blatt {
  const kopf = ['Gliederung', 'Ebene', 'Titel', 'Beschreibung', ...spalten.map((s) => s.name), 'Anlagen', 'Pfad'];
  const arten: Blatt['arten'] = ['text', 'zahl', 'text', 'text', ...spalten.map((s) => (s.typ === 'betrag' ? 'betrag' : s.typ === 'zahl' || s.typ === 'fortschritt' ? 'zahl' : s.typ === 'datum' ? 'datum' : 'text') as 'text'), 'zahl', 'text'];
  const liste = reihenfolge(baum);
  const zeilen: Zelle[][] = liste.map((k) => [
    baum.nummer.get(k.id) ?? '',
    baum.ebene.get(k.id) ?? 0,
    k.titel,
    k.beschreibung || null,
    ...spalten.map((s): Zelle => {
      const w = k.werte[s.id];
      if (w === undefined || w === null) return null;
      if (s.typ === 'datum' && typeof w === 'string') return { datum: w };
      if (s.typ === 'person' && typeof w === 'string') return personen.get(w) ?? null;
      return w;
    }),
    k.anlagen || null,
    pfadText(baum, k.id),
  ]);
  return { name: titel, kopf, zeilen, arten, ebenen: liste.map((k) => baum.ebene.get(k.id) ?? 0), einrueckSpalte: 2 };
}

export function csvDatei(b: Blatt): Blob {
  return new Blob([csvSchreiben(b)], { type: 'text/csv;charset=utf-8' });
}

export async function xlsxDatei(b: Blatt): Promise<Blob> {
  return new Blob([(await xlsxSchreiben(b)) as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// ------------------------------------------------------------------ Datei → Import

const KOPF = {
  ebene: ['ebene', 'level', 'stufe', 'tiefe'],
  gliederung: ['gliederung', 'nr', 'nr.', 'nummer', 'wbs', '#', 'pos', 'pos.'],
  pfad: ['pfad', 'path'],
  titel: ['titel', 'title', 'name', 'punkt', 'thema', 'knoten', 'bezeichnung', 'überschrift'],
  beschreibung: ['beschreibung', 'notiz', 'description', 'details', 'bemerkung', 'bemerkungen', 'kommentar', 'inhalt'],
  weg: ['anlagen'],
};

export interface ImportVorschau {
  eingabe: ImportEingabe;
  /** Spalten, die als eigene Spalten angelegt würden (mit geratener Art). */
  spalten: { name: string; typ: SpaltenTyp }[];
  hinweis: string;
}

function istDatum(w: unknown, datumSpalte: boolean): boolean {
  if (typeof w === 'number') return datumSpalte;
  return typeof w === 'string' && (/^\d{4}-\d{2}-\d{2}/.test(w.trim()) || /^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(w.trim()));
}
function istZahl(w: unknown): boolean {
  if (typeof w === 'number') return true;
  return typeof w === 'string' && /^\s*-?[\d.\s]*\d(,\d+)?\s*(€|eur|euro|\$|chf)?\s*$/i.test(w) && /\d/.test(w);
}

function istProzent(w: unknown): boolean {
  const n = typeof w === 'number' ? w : /^\s*\d{1,3}([,.]\d+)?\s*%?\s*$/.test(String(w)) ? Number(String(w).replace(',', '.').replace('%', '')) : NaN;
  return Number.isFinite(n) && n >= 0 && n <= 100;
}

function artRaten(name: string, werte: unknown[], datumSpalte: boolean, personen: string[]): SpaltenTyp {
  const n = name.toLowerCase();
  const voll = werte.filter((w) => w !== null && w !== undefined && String(w).trim() !== '');
  const alle = (f: (w: unknown) => boolean) => voll.length > 0 && voll.filter(f).length / voll.length >= 0.8;
  if (/betrag|kosten|preis|summe|euro|€|budget|honorar/.test(n) && (voll.length === 0 || alle(istZahl))) return 'betrag';
  if (/termin|datum|fällig|faellig|frist|deadline|bis wann|start|ende/.test(n) && (voll.length === 0 || alle((w) => istDatum(w, datumSpalte)))) return 'datum';
  if (/verantwortlich|zuständig|zustaendig|person|wer|owner|bearbeiter/.test(n)) {
    const namen = personen.map((p) => p.toLowerCase());
    if (voll.length > 0 && voll.every((w) => namen.includes(String(w).trim().toLowerCase()))) return 'person';
  }
  if (/erledigt|fertig|haken|ok\b|done/.test(n) && alle((w) => /^(ja|nein|x|true|false|wahr|falsch|1|0|✓|✔)$/i.test(String(w).trim()))) return 'haken';
  if (/fortschritt|prozent|erledigt %|% erledigt|\bfertig %/.test(n) && voll.length > 0 && alle(istProzent)) return 'fortschritt';
  if (/^(link|url|adresse|webseite|website|internet|quelle)$/.test(n.trim()) || (voll.length > 0 && alle((w) => /^(https?:\/\/|www\.)\S+$/i.test(String(w).trim()))))
    return 'link';
  if (/status|phase|priorität|prioritaet|stand|kategorie/.test(n) && new Set(voll.map(String)).size <= 12) return 'auswahl';
  if (datumSpalte || alle((w) => istDatum(w, datumSpalte))) return 'datum';
  if (alle(istZahl)) return 'zahl';
  return 'text';
}

/** Macht aus gelesenen Zeilen (erste Zeile = Kopf) eine Import-Eingabe mit Ebenen. */
export function importVorbereiten(t: GeleseneTabelle, dateiname: string, personen: string[]): ImportVorschau {
  const [kopfRoh, ...rest] = t.zeilen;
  if (!kopfRoh || !rest.length) throw new Error('Die Datei braucht eine Kopfzeile und mindestens eine Zeile darunter.');
  const kopf = kopfRoh.map((k) => String(k ?? '').trim());
  const finde = (namen: string[]) => kopf.findIndex((k) => namen.includes(k.toLowerCase()));
  const sp = {
    ebene: finde(KOPF.ebene),
    gliederung: finde(KOPF.gliederung),
    pfad: finde(KOPF.pfad),
    titel: finde(KOPF.titel),
    beschreibung: finde(KOPF.beschreibung),
  };
  const belegt = new Set(Object.values(sp).filter((i) => i >= 0));
  kopf.forEach((k, i) => KOPF.weg.includes(k.toLowerCase()) && belegt.add(i));
  if (sp.titel < 0) {
    sp.titel = sp.pfad >= 0 ? -1 : kopf.findIndex((_, i) => !belegt.has(i));
    if (sp.titel >= 0) belegt.add(sp.titel);
  }
  const ebenenAusExcel = t.ebenen.slice(1);
  const einzug = t.einrueckung.slice(1).map((e) => (sp.titel >= 0 ? (e[sp.titel] ?? 0) : 0));
  const eigene = kopf.map((k, i) => ({ k, i })).filter(({ k, i }) => k && !belegt.has(i));

  let quelle = 'alle Zeilen als Unterpunkte';
  const zeilen: ImportZeile[] = [];
  rest.forEach((z, n) => {
    const roh = (i: number) => (i >= 0 ? z[i] : null);
    const text = (i: number) => {
      const w = roh(i);
      return w === null || w === undefined ? '' : String(w);
    };
    let titel = sp.titel >= 0 ? text(sp.titel) : '';
    let ebene: number | null = null;
    if (sp.ebene >= 0 && text(sp.ebene).trim() !== '' && !Number.isNaN(Number(text(sp.ebene)))) {
      ebene = Math.max(0, Math.floor(Number(text(sp.ebene))));
      quelle = 'Spalte „Ebene“';
    } else if (sp.gliederung >= 0) {
      const g = text(sp.gliederung).trim().replace(/\.$/, '');
      ebene = g ? g.split('.').filter(Boolean).length : 0;
      quelle = 'Gliederungsnummern';
    } else if (sp.pfad >= 0 && text(sp.pfad).trim()) {
      const teile = text(sp.pfad).split(/\s*(?:›|>|\/|\\)\s*/).filter(Boolean);
      ebene = Math.max(0, teile.length - 1);
      if (!titel) titel = teile[teile.length - 1] ?? '';
      quelle = 'Pfad';
    } else if (ebenenAusExcel.some((e) => e > 0)) {
      ebene = (ebenenAusExcel[n] ?? 0) + 1;
      quelle = 'Excel-Gruppierung';
    } else if (einzug.some((e) => e > 0)) {
      ebene = (einzug[n] ?? 0) + 1;
      quelle = 'Einrückung';
    } else {
      const m = /^(\s*)/.exec(titel)?.[1] ?? '';
      const tiefe = m.replace(/\t/g, '  ').length;
      if (tiefe) quelle = 'Einrückung';
      ebene = Math.floor(tiefe / 2) + 1;
    }
    const werte: Record<string, string | number | boolean | null> = {};
    for (const { k, i } of eigene) {
      const w = roh(i);
      if (w !== null && w !== undefined && String(w).trim() !== '') werte[k] = w;
    }
    if (titel.trim()) zeilen.push({ ebene, titel: titel.trim(), beschreibung: sp.beschreibung >= 0 ? text(sp.beschreibung) : '', werte });
  });
  // Einrückung per Leerzeichen gilt nur, wenn mindestens eine Zeile eingerückt ist; sonst alles Ebene 1.
  const spalten = eigene.map(({ k, i }) => ({
    name: k,
    typ: artRaten(
      k,
      rest.map((z) => z[i]),
      t.datumSpalten.has(i),
      personen,
    ),
  }));
  const titel = dateiname.replace(/\.(csv|xlsx|txt|tsv)$/i, '');
  return { eingabe: { titel, spalten, zeilen }, spalten, hinweis: `Ebenen erkannt über: ${quelle}.` };
}

// ------------------------------------------------------------------ Mindmap → Bild

const HELL = { text: '#1b1e25', flaeche: '#ffffff', akzent: '#2f5fd0', akzentText: '#ffffff', hg: '#ffffff' };

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Zeichnet die Mindmap (wie gerade ein-/ausgeklappt) als eigenständiges SVG mit hellen Farben. */
export function mindmapSvg(baum: Baum, zu: Set<string>): { svg: string; breite: number; hoehe: number } {
  const a = anordnen(baum, zu);
  const g = a.grenzen;
  const rand = 24;
  const breite = Math.ceil(g.x2 - g.x1 + rand * 2);
  const hoehe = Math.ceil(g.y2 - g.y1 + rand * 2);
  const ton = (id: string) => {
    const f = baum.astFarbe.get(id);
    return f ? FARBTON[f] : HELL.akzent;
  };
  const teile: string[] = [];
  for (const l of a.liste) {
    const k = baum.nachId.get(l.id);
    const e = k?.elternId ? a.lagen.get(k.elternId) : undefined;
    if (e) teile.push(`<path d="${kante(e, l)}" stroke="${ton(l.id)}" stroke-width="${Math.max(1.4, 3.6 - l.tiefe * 0.7)}" fill="none" opacity="0.85"/>`);
  }
  for (const l of a.liste) {
    const k = baum.nachId.get(l.id)!;
    const wurzel = l.seite === 0;
    const eigen = k.farbe ? FARBTON[k.farbe] : null;
    const fuellung = wurzel ? (eigen ?? HELL.akzent) : eigen ? `${eigen}30` : HELL.flaeche;
    const tf = wurzel ? HELL.akzentText : HELL.text;
    const zeile = wurzel ? MASS.zeileWurzel : MASS.zeile;
    const tx = MASS.innenX + (k.symbol ? MASS.symbol + 4 : 0) + (wurzel ? 6 : 0);
    const ty = MASS.innenY + (wurzel ? 5 : 0);
    const st = wurzel ? tf : ton(l.id);
    let x = l.b - MASS.innenX + 4;
    const z: string[] = [];
    z.push(`<rect width="${l.b}" height="${l.h}" rx="${wurzel ? 14 : 10}" fill="${fuellung}" stroke="${wurzel ? 'none' : ton(l.id)}" stroke-width="1.5"/>`);
    if (k.symbol) {
      z.push(
        `<path d="${SYMBOL_PFAD[k.symbol]}" transform="translate(${MASS.innenX + (wurzel ? 6 : 0)},${l.h / 2 - 10}) scale(${20 / 24})" fill="none" stroke="${st}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>`,
      );
    }
    z.push(
      `<text x="${tx}" y="${ty}" fill="${tf}" font-size="${wurzel ? MASS.schriftWurzel : MASS.schrift}" font-weight="${wurzel ? 600 : 500}">${l.zeilen
        .map((t, i) => `<tspan x="${tx}" y="${(ty + i * zeile + zeile * 0.74).toFixed(1)}">${esc(t)}</tspan>`)
        .join('')}</text>`,
    );
    if (k.anlagen) {
      x -= MASS.zeichen;
      z.push(
        `<g transform="translate(${x},${l.h / 2 - 8})"><path d="${KLAMMER_PFAD}" transform="scale(0.62)" fill="none" stroke="${st}" stroke-width="2.6" stroke-linecap="round"/><text x="16" y="12" font-size="11" fill="${tf}">${k.anlagen}</text></g>`,
      );
    }
    if (k.notizen || k.beschreibung) {
      x -= MASS.zeichen - 6;
      z.push(`<g transform="translate(${x},${l.h / 2 - 8})"><path d="${NOTIZ_PFAD}" transform="scale(0.66)" fill="none" stroke="${st}" stroke-width="2.4" stroke-linejoin="round"/></g>`);
    }
    if (l.eingeklappt) {
      const cx = l.seite === -1 ? -9 : l.b + 9;
      z.push(
        `<g transform="translate(${cx},${l.h / 2})"><circle r="10" fill="${HELL.flaeche}" stroke="${ton(l.id)}" stroke-width="1.5"/><text text-anchor="middle" y="4" font-size="10.5" font-weight="700" fill="${ton(l.id)}">${l.versteckt}</text></g>`,
      );
    }
    teile.push(`<g transform="translate(${l.x.toFixed(1)},${l.y.toFixed(1)})">${z.join('')}</g>`);
  }
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${breite}" height="${hoehe}" viewBox="${g.x1 - rand} ${g.y1 - rand} ${breite} ${hoehe}" font-family="${esc(schrift())}">
<rect x="${g.x1 - rand}" y="${g.y1 - rand}" width="${breite}" height="${hoehe}" fill="${HELL.hg}"/>
${teile.join('\n')}
</svg>`;
  return { svg, breite, hoehe };
}

export async function mindmapPng(svg: string, breite: number, hoehe: number): Promise<Blob> {
  const faktor = Math.min(2, 8000 / Math.max(breite, hoehe));
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const bild = new Image();
    await new Promise<void>((ok, nein) => {
      bild.onload = () => ok();
      bild.onerror = () => nein(new Error('Bild konnte nicht erzeugt werden'));
      bild.src = url;
    });
    const c = document.createElement('canvas');
    c.width = Math.round(breite * faktor);
    c.height = Math.round(hoehe * faktor);
    const ctx = c.getContext('2d')!;
    ctx.scale(faktor, faktor);
    ctx.drawImage(bild, 0, 0, breite, hoehe);
    return await new Promise<Blob>((ok, nein) => c.toBlob((b) => (b ? ok(b) : nein(new Error('Bild konnte nicht erzeugt werden'))), 'image/png'));
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ------------------------------------------------------------------ Drucken

function drucken(titel: string, inhalt: string, quer: boolean) {
  const f = document.createElement('iframe');
  f.setAttribute('aria-hidden', 'true');
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  f.srcdoc = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(titel)}</title><style>
@page { size: A4 ${quer ? 'landscape' : 'portrait'}; margin: 12mm; }
html, body { margin: 0; font-family: ${esc(schrift())}; color: #1b1e25; }
h1 { font-size: 15pt; margin: 0 0 6mm; }
svg { width: 100%; height: auto; max-height: ${quer ? 170 : 255}mm; }
table { border-collapse: collapse; width: 100%; font-size: 9pt; }
th, td { border: 1px solid #c9ced8; padding: 3px 5px; text-align: left; vertical-align: top; }
th { background: #eef0f4; }
td.zahl { text-align: right; white-space: nowrap; }
tr { break-inside: avoid; }
</style></head><body><h1>${esc(titel)}</h1>${inhalt}</body></html>`;
  f.onload = () => {
    f.contentWindow?.focus();
    f.contentWindow?.print();
    setTimeout(() => f.remove(), 60_000);
  };
  document.body.appendChild(f);
}

export function mindmapDrucken(baum: Baum, zu: Set<string>, titel: string) {
  const s = mindmapSvg(baum, zu);
  drucken(titel, s.svg.replace(/^<\?xml[^>]*>\s*/, ''), s.breite >= s.hoehe);
}

/** HTML-Tabelle zum Drucken (ohne die Spalten „Ebene“ und „Pfad“, Titel eingerückt). */
export function tabelleAlsHtml(b: Blatt): string {
  const zahl = (i: number) => b.arten?.[i] === 'zahl' || b.arten?.[i] === 'betrag';
  const zelle = (w: Zelle, i: number, ebene: number) => {
    let t = '';
    if (w === null || w === undefined) t = '';
    else if (typeof w === 'object') t = w.datum.replace(/^(\d{4})-(\d{2})-(\d{2}).*/, '$3.$2.$1');
    else if (typeof w === 'boolean') t = w ? '✓' : '';
    else if (typeof w === 'number')
      t = b.arten?.[i] === 'betrag' ? w.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : w.toLocaleString('de-DE');
    else t = w;
    const stil = i === b.einrueckSpalte ? ` style="padding-left:${5 + ebene * 14}px${ebene === 0 ? ';font-weight:600' : ''}"` : '';
    return `<td${zahl(i) ? ' class="zahl"' : ''}${stil}>${esc(t).replace(/\n/g, '<br>')}</td>`;
  };
  const zeigen = b.kopf.map((_, i) => i).filter((i) => b.kopf[i] !== 'Ebene' && b.kopf[i] !== 'Pfad');
  const kopf = `<tr>${zeigen.map((i) => `<th>${esc(b.kopf[i]!)}</th>`).join('')}</tr>`;
  const zeilen = b.zeilen.map((z, n) => `<tr>${zeigen.map((i) => zelle(z[i] ?? null, i, b.ebenen?.[n] ?? 0)).join('')}</tr>`).join('');
  return `<table><thead>${kopf}</thead><tbody>${zeilen}</tbody></table>`;
}

export function tabelleDrucken(b: Blatt) {
  drucken(b.name, tabelleAlsHtml(b), b.kopf.length > 7);
}
